/**
 * Mic capture with MediaRecorder + Web Audio level meter.
 *
 * Picks the first supported audio MIME, opens the user's microphone, and
 * runs an AnalyserNode poll loop to emit a normalized 0..1 level on each
 * tick. stop() resolves once MediaRecorder.onstop has fired with the
 * joined blob, duration, and chosen MIME.
 */

const MIME_CANDIDATES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4;codecs=mp4a.40.2",
  "audio/mp4",
  "audio/ogg;codecs=opus",
];

const LEVEL_POLL_MS = 100;

// RMS of normal speech sits well below full scale; amplify so the meter
// reacts to ordinary (not just loud) talking while staying near zero during
// silence. Loud speech pins to full, which is fine for a level meter.
const LEVEL_GAIN = 6;

export function pickAudioMime() {
  if (typeof MediaRecorder === "undefined") return null;
  for (const m of MIME_CANDIDATES) {
    if (MediaRecorder.isTypeSupported(m)) return m;
  }
  return "";
}

export async function startMicCapture({ onLevel }) {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  const mime = pickAudioMime();

  const recorder = mime
    ? new MediaRecorder(stream, { mimeType: mime })
    : new MediaRecorder(stream);

  const chunks = [];
  recorder.ondataavailable = (e) => {
    if (e && e.data) chunks.push(e.data);
  };

  const audioCtx = new AudioContext();
  // Created after the awaited getUserMedia above, so we're off the original
  // user-gesture tick — Chrome's autoplay policy starts the context
  // "suspended", and a suspended AnalyserNode only ever yields zeros (a dead
  // level meter). Resume it so the meter reflects the mic.
  if (audioCtx.state === "suspended") {
    try { await audioCtx.resume(); } catch (_) { /* meter is best-effort */ }
  }
  const source = audioCtx.createMediaStreamSource(stream);
  const analyser = audioCtx.createAnalyser();
  source.connect(analyser);
  // Time-domain samples, not frequency bins: a frequency-bin average dilutes
  // speech (which occupies a small slice of the spectrum) across ~1000 mostly
  // silent bins, so the level barely moves. RMS of the waveform tracks actual
  // loudness.
  const buf = new Uint8Array(analyser.fftSize);

  let stopped = false;
  const levelInterval = setInterval(() => {
    if (stopped) return;
    analyser.getByteTimeDomainData(buf);
    let sumSq = 0;
    for (let i = 0; i < buf.length; i++) {
      const v = (buf[i] - 128) / 128;  // center on the 128 zero-point, normalize to -1..1
      sumSq += v * v;
    }
    const rms = buf.length ? Math.sqrt(sumSq / buf.length) : 0;
    const level = Math.min(1, rms * LEVEL_GAIN);
    if (typeof onLevel === "function") onLevel(level);
  }, LEVEL_POLL_MS);

  const startedAt = Date.now();
  recorder.start();

  let stopResultPromise = null;

  function stop() {
    if (stopResultPromise) return stopResultPromise;
    stopResultPromise = new Promise((resolve) => {
      stopped = true;
      clearInterval(levelInterval);

      const finalize = () => {
        const blob = new Blob(chunks, mime ? { type: mime } : undefined);
        const durationMs = Date.now() - startedAt;
        for (const track of stream.getTracks()) track.stop();
        try {
          audioCtx.close();
        } catch (_) {
          /* ignore */
        }
        resolve({ blob, durationMs, mime });
      };

      const prevOnStop = recorder.onstop;
      recorder.onstop = (ev) => {
        if (typeof prevOnStop === "function") prevOnStop(ev);
        finalize();
      };

      recorder.stop();
    });
    return stopResultPromise;
  }

  return { mime, stop };
}
